/**
 * English: Root React Router map, auth-gated routes, and global AuthProvider/StationProvider.
 * Thai: แผนที่ route หลัก, route ที่ต้อง login, และ AuthProvider/StationProvider ระดับแอป
 */
import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { StationProvider } from './context/StationContext';
import { LayoutProvider } from './context/LayoutContext';
import { AppToaster } from './components/shared/Toast';
import Layout from './components/layout/Layout';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import ChatTestPage from './pages/ChatTestPage';
import OrganizationsPage from './pages/OrganizationsPage';
import SettingsPage from './pages/SettingsPage';
import SystemStatusPage from './pages/SystemStatusPage';
import UsagePage from './pages/UsagePage';
import MemorySummaryPage from './pages/MemorySummaryPage';
import DocsPage from './pages/DocsPage';
import NotFoundPage from './pages/NotFoundPage';
import TaskDetailPage from './pages/TaskDetailPage';
import TaskInboxPage from './pages/TaskInboxPage';
import AgentManagementPage from './pages/AgentManagementPage';
import AgentDetailPage from './pages/AgentDetailPage';
import StationManagerDetailPage from './pages/StationManagerDetailPage';
import ToolsPage from './pages/ToolsPage';
import GuardrailsPage from './pages/GuardrailsPage';

import { routerBasename, safeReturnPath } from './config/publicPath';

function ProtectedRoutes() {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  return (
    <StationProvider>
      <LayoutProvider>
        <Routes>
          <Route path="/" element={<Layout />}>
            <Route index element={<DashboardPage />} />
            <Route path="organizations" element={<OrganizationsPage />} />
            <Route path="chat-test" element={<ChatTestPage />} />
            <Route path="agent-management" element={<AgentManagementPage />} />
            <Route path="agent-management/manager" element={<StationManagerDetailPage />} />
            <Route path="agent-management/:agentId" element={<AgentDetailPage />} />
            <Route path="tools" element={<ToolsPage />} />
            <Route path="docs" element={<DocsPage />} />
            <Route path="status" element={<SystemStatusPage />} />
            <Route path="usage" element={<UsagePage />} />
            <Route path="memory-summary" element={<MemorySummaryPage />} />
            <Route path="guardrails" element={<GuardrailsPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="tasks/:taskId" element={<TaskDetailPage />} />
            <Route path="tasks" element={<TaskInboxPage />} />
            <Route path="task-history" element={<Navigate to="/tasks" replace />} />
            <Route path="agents" element={<Navigate to="/agent-management" replace />} />
            <Route path="chatbot-docs" element={<Navigate to="/docs" replace />} />
            <Route path="warn" element={<Navigate to="/chat-test" replace />} />
            <Route path="404" element={<NotFoundPage />} />
            <Route path="*" element={<Navigate to="/404" replace />} />
          </Route>
        </Routes>
      </LayoutProvider>
    </StationProvider>
  );
}

function LoginRoute() {
  const { isAuthenticated } = useAuth();
  const [params] = useSearchParams();
  const returnTo = safeReturnPath(params.get('returnTo'));
  if (isAuthenticated) return <Navigate to={returnTo} replace />;
  return <LoginPage />;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter basename={routerBasename}>
          <Routes>
            <Route path="/login" element={<LoginRoute />} />
            <Route path="/*" element={<ProtectedRoutes />} />
          </Routes>
        </BrowserRouter>
        <AppToaster />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
